import type { FieldTemplateProps, RJSFSchema } from '@rjsf/utils';
import React, { useState, useEffect } from 'react';
import { Checkbox, Form, Message } from 'semantic-ui-react';

/**
 * Check if a schema represents a nullable object at the root level
 * @param schema - The JSON Schema to check
 * @returns True if the schema is a nullable object
 */
export const isNullableObjectSchema = (schema: RJSFSchema): boolean => {
  if (!schema) return false;

  // Check if schema has anyOf with both null and object types
  if (schema.anyOf && Array.isArray(schema.anyOf)) {
    const hasNull = schema.anyOf.some(
      subSchema => typeof subSchema === 'object' && subSchema?.type === 'null',
    );
    const hasObject = schema.anyOf.some(
      subSchema =>
        typeof subSchema === 'object' && subSchema?.type === 'object',
    );
    return hasNull && hasObject;
  }

  return false;
};
/**
 * Custom field template for nullable objects that adds an enable/disable
 * checkbox. When the checkbox is unchecked, the entire object is set to `null`.
 * When checked, the object fields are shown and can be edited.
 *
 * This component automatically detects nullable objects by checking if the
 * schema has an `anyOf` with a null type option.
 *
 * ## Usage
 *
 * This component is automatically applied by RJSF when a schema contains a
 * nullable object. It's registered as a custom `FieldTemplate` in the RJSF
 * configuration and will be used for any field that matches the nullable object
 * pattern.
 *
 * ### Schema Pattern
 *
 * The component detects nullable objects using the following JSON Schema
 * pattern:
 *
 * ```json
 * {
 *   "anyOf": [
 *     { "type": "null" },
 *     {
 *       "type": "object",
 *       "properties": { ... }
 *     }
 *   ]
 * }
 * ```
 *
 * ### Integration
 *
 * This template is registered in the RJSF theme configuration (typically in
 * `json-field-editor.tsx` or similar) and automatically applies to matching
 * schemas:
 *
 * ```tsx
 * const theme = {
 *   templates: {
 *     FieldTemplate: NullableObjectFieldTemplate,
 *   },
 * };
 * ```
 *
 * ### Example Use Cases
 *
 * - **Optional Configuration Sections**: Settings that can be completely
 *   disabled (e.g., authentication lock, custom links, custom message)
 * - **Feature Toggles**: Object-based features that can be turned on/off
 * - **Conditional Settings**: Configuration blocks that may or may not be needed
 *
 * @example
 * // UI config schema with nullable authentication lock
 * const authenticationLock = z.object({
 *   count: z.number().int().min(1),
 *   expires: z.number().int().min(1),
 * }).nullable().default(null);
 *
 * When rendered in the UI:
 * - Checkbox: "Enable this configuration"
 * - When unchecked: `formData = null`
 * - When checked: `formData = { count: 0, expires: 0 }` (or user values)
 */
export const NullableObjectFieldTemplate = (props: FieldTemplateProps) => {
  const {
    label,
    children,
    required,
    schema,
    formData,
    onChange,
    rawDescription,
    rawErrors,
    rawHelp,
    displayLabel,
    uiSchema,
  } = props;

  // Check if this field is a nullable object
  const isNullableObject = React.useMemo(
    () => isNullableObjectSchema(schema),
    [schema],
  );

  // State for whether the object is enabled (not `null`)
  const [isEnabled, setIsEnabled] = useState<boolean>(
    formData !== null && formData !== undefined,
  );

  // Update enabled state when formData changes externally
  useEffect(() => {
    setIsEnabled(formData !== null && formData !== undefined);
  }, [formData]);

  // Handle the checkbox toggle
  const handleToggle = (e: React.FormEvent, data: any) => {
    const newEnabled = data.checked;
    setIsEnabled(newEnabled);

    if (!newEnabled) {
      // Set to `null` when disabled
      onChange(null);
    } else {
      // Initialize with empty object when enabled. The form will populate with
      // default values from the schema.
      onChange({});
    }
  };

  // If not a nullable object, render normally
  if (!isNullableObject) {
    return (
      <Form.Field
        required={required}
        error={rawErrors && rawErrors.length > 0}
        className="rjsf-field-wrapper"
      >
        {children}
        {rawDescription && displayLabel && (
          <small className="rjsf-field-description">{rawDescription}</small>
        )}
        {rawErrors && rawErrors.length > 0 && (
          <div className="ui pointing above red basic label rjsf-field-error-label">
            {rawErrors.join(', ')}
          </div>
        )}
        {rawHelp && <small className="rjsf-field-help">{rawHelp}</small>}
      </Form.Field>
    );
  }

  // For nullable objects, render with enable/disable checkbox
  return (
    <Form.Field
      required={required}
      error={rawErrors && rawErrors.length > 0}
      className="rjsf-nullable-object-field"
    >
      {/* Enable/Disable checkbox */}
      <div className="rjsf-nullable-object-header">
        <Checkbox
          toggle
          label={label || 'Enable this configuration'}
          checked={isEnabled}
          onChange={handleToggle}
          disabled={uiSchema?.['ui:disabled']}
        />
      </div>

      {/* Show description when enabled */}
      {isEnabled && rawDescription && (
        <small className="rjsf-field-description">{rawDescription}</small>
      )}

      {/* Show children (object fields) only when enabled */}
      {isEnabled && (
        <div className="rjsf-nullable-object-content">{children}</div>
      )}

      {/* Show a message when disabled */}
      {!isEnabled && (
        <Message info size="small">
          <Message.Content>
            This configuration is currently disabled. Enable it to configure the
            settings.
          </Message.Content>
        </Message>
      )}

      {/* Error messages */}
      {rawErrors && rawErrors.length > 0 && (
        <div className="ui pointing above red basic label rjsf-field-error-label">
          {rawErrors.join(', ')}
        </div>
      )}

      {/* Help text */}
      {rawHelp && <small className="rjsf-field-help">{rawHelp}</small>}
    </Form.Field>
  );
};
